import React, {useEffect, useState} from "react";
import {SpaceTopAndBottom} from "../../../z40-uicomp/layout";
import {CenterSpinner} from "@soap/baseweb";
import {useAuth, useEvent, useLazyQuery} from "@soap/modules";
import {Roles} from "../../../hooks/useEnumerations";
import {ArchiveUserDialog} from "../dialogs/ArchiveUserDialog";
import {UnarchiveUserDialog} from "../dialogs/UnarchiveUserDialog";
import {GrantAdminDialog} from "../dialogs/GrantAdminDialog";
import {RevokeAdminDialog} from "../dialogs/RevokeAdminDialog";
import {useUserRole} from "../../../hooks/useUserRole";
import {Card, CardContent, CardHeader, CardTitle} from "../../ui/card";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "../../ui/table";
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from "../../ui/dropdown-menu";
import {Button} from "../../ui/button";
import {MoreHorizontal} from "lucide-react";

export function Users() {
    const [getOrg, gotOrg] = useLazyQuery("C241v1_GetOrganisationDetails");
    const {requestingUserIsOrgAdmin, isOrgUser} = useUserRole();
    const viewStates = {
        loading: 1,
        loaded: 2
    };

    const [viewState, setViewState] = useState(viewStates.loading);
    
    // Only fetch org details if user has organization access
    const hasOrgAccess = isOrgUser || requestingUserIsOrgAdmin;

    useEffect(() => {
        if (hasOrgAccess) {
            getOrg({});
        } else {
            // User doesn't have org access, set to loaded state without data
            setViewState(viewStates.loaded);
        }
    }, [hasOrgAccess, getOrg]);

    useEffect(() => {
        if (gotOrg) {
            setViewState(viewStates.loaded);
        }
    }, [gotOrg]);

    useEvent({
        eventName: "E194v1_OrganisationNameUpdated",
        onEventReceived: () => {
            if (hasOrgAccess) {
                getOrg({});
            }
        }
    }, [hasOrgAccess, getOrg]);

    useEvent({
        eventName: "E197v1_OrganisationMembersUpdated",
        onEventReceived: () => {
            if (hasOrgAccess) {
                getOrg({});
            }
        }
    }, [hasOrgAccess, getOrg]);
    
    // If user doesn't have org access, don't render the component
    if (!hasOrgAccess) {
        return null;
    }
    
    const users = gotOrg?.e193_SubAccounts || [];

    switch (viewState) {
        case viewStates.loading:
            return <CenterSpinner/>;
        case viewStates.loaded:
            return (<> 
                <SpaceTopAndBottom>
                    <Card>
                        <CardHeader>
                            <CardTitle>User Accounts</CardTitle>
                        </CardHeader>
                        <CardContent className="p-6">
                            {users.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Name</TableHead>
                                                <TableHead>Email</TableHead>
                                                <TableHead>Role</TableHead>
                                                <TableHead>Active</TableHead>
                                                <TableHead className="w-[50px] sticky right-0 bg-white z-10">Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {users.map((user: any) => {
                                                // Calculate if there will be any menu items
                                                const hasMenuItems = !user.e193_IsRequestingUser && requestingUserIsOrgAdmin && (
                                                    (user.e193_Active && user.e193_RoleKeys?.includes(Roles.Teacher)) || // ArchiveUser
                                                    (!user.e193_Active && user.e193_RoleKeys?.includes(Roles.Teacher)) || // UnarchiveUser
                                                    (!user.e193_RoleKeys?.includes(Roles.OrgAdmin) && 
                                                     user.e193_RoleKeys?.includes(Roles.Teacher) && 
                                                     user.e193_Active) || // GrantAdmin
                                                    (user.e193_RoleKeys?.includes(Roles.OrgAdmin) && user.e193_Active) // RevokeAdmin
                                                );

                                                return (
                                                    <TableRow 
                                                        key={user.e193_SubAccountId}
                                                        className={!user.e193_Active ? 'opacity-60' : ''}
                                                    >
                                                        <TableCell className="font-medium">
                                                            {user.e193_Active ? (
                                                                user.e193_Name
                                                            ) : (
                                                                <span className="line-through">{user.e193_Name}</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="break-words max-w-[200px]">
                                                            {user.e193_Email || <span className="text-gray-400">—</span>}
                                                        </TableCell>
                                                        <TableCell>
                                                            {user.e193_Role}
                                                        </TableCell>
                                                        <TableCell>
                                                            {user.e193_Active ? "Y" : "N"}
                                                        </TableCell>
                                                        <TableCell className="sticky right-0 bg-white z-10">
                                                            {hasMenuItems && (
                                                                <DropdownMenu>
                                                                    <DropdownMenuTrigger asChild>
                                                                        <Button variant="ghost" size="sm">
                                                                            <MoreHorizontal className="w-4 h-4" />
                                                                        </Button>
                                                                    </DropdownMenuTrigger>
                                                                    <DropdownMenuContent align="end">
                                                                        {user.e193_Active && user.e193_RoleKeys?.includes(Roles.Teacher) && (
                                                                            <ArchiveUserDialog 
                                                                                tenantUserId={user.e193_SubAccountId}
                                                                                tenantUser={user.e193_Name}
                                                                            />
                                                                        )}
                                                                        {!user.e193_Active && user.e193_RoleKeys?.includes(Roles.Teacher) && (
                                                                            <UnarchiveUserDialog 
                                                                                tenantUserId={user.e193_SubAccountId}
                                                                                tenantUser={user.e193_Name}
                                                                            />
                                                                        )}
                                                                        {!user.e193_RoleKeys?.includes(Roles.OrgAdmin) && 
                                                                         user.e193_RoleKeys?.includes(Roles.Teacher) && 
                                                                         user.e193_Active && (
                                                                            <GrantAdminDialog 
                                                                                tenantUserId={user.e193_SubAccountId}
                                                                                tenantUser={user.e193_Name}
                                                                            />
                                                                        )}
                                                                        {user.e193_RoleKeys?.includes(Roles.OrgAdmin) && user.e193_Active && (
                                                                            <RevokeAdminDialog 
                                                                                tenantUserId={user.e193_SubAccountId}
                                                                                tenantUser={user.e193_Name}
                                                                            />
                                                                        )}
                                                                    </DropdownMenuContent>
                                                                </DropdownMenu>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="text-center py-8 text-gray-500">
                                    No users found
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </SpaceTopAndBottom>
            </>);
    }
}

